import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  TextField,
  Typography,
  Box,
  Button,
  Checkbox,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import PropTypes from "prop-types";

const OemTargetPopup = ({
  open,
  onClose,
  allCountries,
  selectedCountries,
  setSelectedCountries,
  reviewStatus,
  handleSave,
}) => {
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [isModified, setIsModified] = useState(false);
  const [includeAllChecked, setIncludeAllChecked] = useState(false);
  const [removeAllChecked, setRemoveAllChecked] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [reasonForExclusion, setReasonForExclusion] = useState(""); // New state for reason

  const [localSelectedCountries, setLocalSelectedCountries] = useState([]);

  const handleSaveClick = () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      setSelectedCountries(localSelectedCountries);

      setIsModified(false);

      if (handleSave && typeof handleSave === "function") {
        handleSave("country", localSelectedCountries, {
          reasonForCountryExclusion: reasonForExclusion || undefined,
        });
      }

      onClose();
    } catch {
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset local state to original values when cancelled
    setLocalSelectedCountries([...selectedCountries]);
    setIsModified(false);
    setReasonForExclusion(""); // Reset reason when cancelling
    onClose();
  };

  const isRestricted =
    reviewStatus.includes("FORCE_UNINSTALL") || reviewStatus.includes("DELIST");

  // Initialize local state when popup opens or selectedCountries changes
  useEffect(() => {
    if (open) {
      setLocalSelectedCountries([...selectedCountries]);
      setIsModified(false);
      setSearch("");
      setReasonForExclusion(""); // Reset reason when opening popup
    }
  }, [open, selectedCountries]);

  useEffect(() => {
    // Check if all countries are selected (based on local state)
    const allIncluded =
      allCountries.length > 0 &&
      allCountries.length === localSelectedCountries.length;
    setIncludeAllChecked(allIncluded);

    // Check if all countries are removed (based on local state)
    const allRemoved = localSelectedCountries.length === 0;
    setRemoveAllChecked(allRemoved);
  }, [localSelectedCountries, allCountries]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth={false}
      slotProps={{
        paper: {
          sx: { width: "585px", height: "470px" },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "4px 24px",
        }}
      >
        Targeting - Country Availability{" "}
        <IconButton onClick={handleCancel}>
          <ClearIcon sx={{ color: "#2A2C2D" }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ overflowY: "hidden" }}>
        <TextField
          fullWidth
          placeholder="Search Country"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: "0px", mt: "0px" }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
        <Tabs
          sx={{
            marginTop: "-4px",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: "bold",
              fontSize: "16px",
              color: "#2A2C2D",
            },
            "& .Mui-selected ": {
              color: "#1976d2 !important",
            },
          }}
          value={tab}
          onChange={(e, newVal) => setTab(newVal)}
        >
          <Tab
            label={`Available (${
              allCountries.length - localSelectedCountries.length
            })`}
          />
          <Tab label={`Targeted (${localSelectedCountries.length})`} />
        </Tabs>

        <Box
          sx={{
            mt: 2,
            height: tab === 1 ? "200px" : "269px",
            overflowY: allCountries.length > 6 ? "scroll" : "hidden",
            overflowX: "hidden",
            paddingRight: "20px",
          }}
        >
          {tab === 0
            ? // Available tab - show ALL countries with + or - based on selection status
              allCountries
                .filter((c) => c.toLowerCase().includes(search.toLowerCase()))
                .map((code) => {
                  const isSelected = localSelectedCountries.includes(code);
                  return (
                    <Box
                      key={code}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        backgroundColor: "#F3F3F3",
                        width: "98%",
                        "&:hover": {
                          backgroundColor: "#E8E8E8",
                          borderRadius: "4px",
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: "14px" }}>{code}</Typography>
                      {!isRestricted && (
                        <Typography
                          sx={{
                            cursor: "pointer",
                            fontWeight: "bold",
                            fontSize: "18px",
                            color: isSelected ? "#B50B00" : "#007BFF",
                          }}
                          onClick={() => {
                            if (isSelected) {
                              // Remove from local state
                              setLocalSelectedCountries(
                                localSelectedCountries.filter((c) => c !== code)
                              );
                            } else {
                              // Add to local state
                              setLocalSelectedCountries([
                                ...localSelectedCountries,
                                code,
                              ]);
                            }
                            setIsModified(true);
                          }}
                        >
                          {isSelected ? (
                            // Minus icon
                            <svg
                              width="16"
                              height="17"
                              viewBox="0 0 16 17"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M4 9.19209H12V7.59209H4V9.19209ZM8 16.3921C6.89333 16.3921 5.85333 16.1821 4.88 15.7621C3.90667 15.3421 3.06 14.7721 2.34 14.0521C1.62 13.3321 1.05 12.4854 0.63 11.5121C0.21 10.5388 0 9.49876 0 8.39209C0 7.28542 0.21 6.24542 0.63 5.27209C1.05 4.29876 1.62 3.45209 2.34 2.73209C3.06 2.01209 3.90667 1.44209 4.88 1.02209C5.85333 0.60209 6.89333 0.39209 8 0.39209C9.10667 0.39209 10.1467 0.60209 11.12 1.02209C12.0933 1.44209 12.94 2.01209 13.66 2.73209C14.38 3.45209 14.95 4.29876 15.37 5.27209C15.79 6.24542 16 7.28542 16 8.39209C16 9.49876 15.79 10.5388 15.37 11.5121C14.95 12.4854 14.38 13.3321 13.66 14.0521C12.94 14.7721 12.0933 15.3421 11.12 15.7621C10.1467 16.1821 9.10667 16.3921 8 16.3921ZM8 14.7921C9.78667 14.7921 11.3 14.1721 12.54 12.9321C13.78 11.6921 14.4 10.1788 14.4 8.39209C14.4 6.60542 13.78 5.09209 12.54 3.85209C11.3 2.61209 9.78667 1.99209 8 1.99209C6.21333 1.99209 4.7 2.61209 3.46 3.85209C2.22 5.09209 1.6 6.60542 1.6 8.39209C1.6 10.1788 2.22 11.6921 3.46 12.9321C4.7 14.1721 6.21333 14.7921 8 14.7921Z"
                                fill="#D60B00"
                              />
                            </svg>
                          ) : (
                            // Plus icon
                            <svg
                              width="16"
                              height="17"
                              viewBox="0 0 16 17"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M7.2 12.3921H8.8V9.19209H12V7.59209H8.8V4.39209H7.2V7.59209H4V9.19209H7.2V12.3921ZM8 16.3921C6.89333 16.3921 5.85333 16.1821 4.88 15.7621C3.90667 15.3421 3.06 14.7721 2.34 14.0521C1.62 13.3321 1.05 12.4854 0.63 11.5121C0.21 10.5388 0 9.49876 0 8.39209C0 7.28542 0.21 6.24542 0.63 5.27209C1.05 4.29876 1.62 3.45209 2.34 2.73209C3.06 2.01209 3.90667 1.44209 4.88 1.02209C5.85333 0.60209 6.89333 0.39209 8 0.39209C9.10667 0.39209 10.1467 0.60209 11.12 1.02209C12.0933 1.44209 12.94 2.01209 13.66 2.73209C14.38 3.45209 14.95 4.29876 15.37 5.27209C15.79 6.24542 16 7.28542 16 8.39209C16 9.49876 15.79 10.5388 15.37 11.5121C14.95 12.4854 14.38 13.3321 13.66 14.0521C12.94 14.7721 12.0933 15.3421 11.12 15.7621C10.1467 16.1821 9.10667 16.3921 8 16.3921ZM8 14.7921C9.78667 14.7921 11.3 14.1721 12.54 12.9321C13.78 11.6921 14.4 10.1788 14.4 8.39209C14.4 6.60542 13.78 5.09209 12.54 3.85209C11.3 2.61209 9.78667 1.99209 8 1.99209C6.21333 1.99209 4.7 2.61209 3.46 3.85209C2.22 5.09209 1.6 6.60542 1.6 8.39209C1.6 10.1788 2.22 11.6921 3.46 12.9321C4.7 14.1721 6.21333 14.7921 8 14.7921Z"
                                fill="#0476E2"
                              />
                            </svg>
                          )}
                        </Typography>
                      )}
                    </Box>
                  );
                })
            : // Selected tab - show only selected countries with minus button
              localSelectedCountries
                .filter((c) => c.toLowerCase().includes(search.toLowerCase()))
                .map((code) => (
                  <Box
                    key={code}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 12px",
                      backgroundColor: "#F3F3F3",
                      width: "98%",
                      "&:hover": {
                        backgroundColor: "#E8E8E8",
                        borderRadius: "4px",
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: "14px" }}>{code}</Typography>
                    {!isRestricted && (
                      <Typography
                        sx={{
                          cursor: "pointer",
                          fontWeight: "bold",
                          fontSize: "18px",
                          color: "#B50B00",
                        }}
                        onClick={() => {
                          // Remove from local state only
                          setLocalSelectedCountries(
                            localSelectedCountries.filter((c) => c !== code)
                          );
                          setIsModified(true);
                        }}
                      >
                        <svg
                          width="16"
                          height="17"
                          viewBox="0 0 16 17"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4 9.19209H12V7.59209H4V9.19209ZM8 16.3921C6.89333 16.3921 5.85333 16.1821 4.88 15.7621C3.90667 15.3421 3.06 14.7721 2.34 14.0521C1.62 13.3321 1.05 12.4854 0.63 11.5121C0.21 10.5388 0 9.49876 0 8.39209C0 7.28542 0.21 6.24542 0.63 5.27209C1.05 4.29876 1.62 3.45209 2.34 2.73209C3.06 2.01209 3.90667 1.44209 4.88 1.02209C5.85333 0.60209 6.89333 0.39209 8 0.39209C9.10667 0.39209 10.1467 0.60209 11.12 1.02209C12.0933 1.44209 12.94 2.01209 13.66 2.73209C14.38 3.45209 14.95 4.29876 15.37 5.27209C15.79 6.24542 16 7.28542 16 8.39209C16 9.49876 15.79 10.5388 15.37 11.5121C14.95 12.4854 14.38 13.3321 13.66 14.0521C12.94 14.7721 12.0933 15.3421 11.12 15.7621C10.1467 16.1821 9.10667 16.3921 8 16.3921ZM8 14.7921C9.78667 14.7921 11.3 14.1721 12.54 12.9321C13.78 11.6921 14.4 10.1788 14.4 8.39209C14.4 6.60542 13.78 5.09209 12.54 3.85209C11.3 2.61209 9.78667 1.99209 8 1.99209C6.21333 1.99209 4.7 2.61209 3.46 3.85209C2.22 5.09209 1.6 6.60542 1.6 8.39209C1.6 10.1788 2.22 11.6921 3.46 12.9321C4.7 14.1721 6.21333 14.7921 8 14.7921Z"
                            fill="#D60B00"
                          />
                        </svg>
                      </Typography>
                    )}
                  </Box>
                ))}
        </Box>

        {/* Reason for Exclusion field - Show only in Selected tab */}
        {tab === 1 && (
          <Box sx={{ mt: "8px", mb: "6px" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 500, mb: 1 }}>
              Reason for Exclusion
            </Typography>
            <TextField
              fullWidth
              placeholder="Reason for country exclusion"
              value={reasonForExclusion}
              onChange={(e) => setReasonForExclusion(e.target.value)}
              disabled={isRestricted}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                  height: "40px",
                  "& input": {
                    padding: "8px 14px",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: tab === 1 ? "8px" : "10px",
            marginBottom: "16px", // Added bottom margin for spacing
          }}
        >
          {tab === 0 ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                onClick={() => {
                  if (!isRestricted) {
                    const availableCountries = allCountries.filter(
                      (c) => !localSelectedCountries.includes(c)
                    );
                    setLocalSelectedCountries([
                      ...localSelectedCountries,
                      ...availableCountries,
                    ]);
                    setIsModified(true);
                    setIncludeAllChecked(true);
                  }
                }}
                checked={includeAllChecked}
                disabled={isRestricted || includeAllChecked}
                sx={{ padding: 0, marginRight: 1, cursor: "pointer" }}
              />
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color:
                    isRestricted || includeAllChecked ? "#BDBDBD" : "#007BFF",
                  pointerEvents: isRestricted ? "none" : "auto",
                }}
              >
                Include All
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                onClick={() => {
                  if (!isRestricted) {
                    setLocalSelectedCountries([]);
                    setIsModified(true);
                  }
                }}
                checked={removeAllChecked}
                disabled={isRestricted || removeAllChecked}
                sx={{ padding: 0, marginRight: 1, cursor: "pointer" }}
              />
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color:
                    isRestricted || removeAllChecked ? "#BDBDBD" : "#007BFF",
                  pointerEvents: isRestricted ? "none" : "auto",
                }}
              >
                Remove All
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              onClick={handleCancel}
              disabled={isSaving}
              sx={{
                textTransform: "none",
                color: "#2A2C2D",
                width: "114px",
                height: "36px",
                backgroundColor: "#1214151A",
              }}
            >
              Cancel
            </Button>
            {!isRestricted && (
              <Button
                variant="contained"
                onClick={handleSaveClick}
                disabled={
                  !isModified || removeAllChecked || isRestricted || isSaving
                }
                sx={{ textTransform: "none", width: "114px", height: "36px" }}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OemTargetPopup;

OemTargetPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  allCountries: PropTypes.array,
  selectedCountries: PropTypes.array,
  setSelectedCountries: PropTypes.func,
  reviewStatus: PropTypes.string,
  handleSave: PropTypes.func,
};
